'use client';

import { SystemLoadingIcon as Loader2 } from '@/components/ui/SystemLoading';
import { CheckCircle2, RefreshCw, Unplug, XCircle } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { StudioAction } from '@/components/ui/StudioAction';
import { useAuthStore } from '@/stores/authStore';
import { getAuthScopeRevision } from '@/lib/auth-scoped-state-reset';
import styles from './SocialAccountManager.module.css';
import { cn } from '@/lib/cn';
import { toast } from '@/lib/stores/toast-store';
import type { ConsumerSocialPublishPlatform } from '@/types/social-publishing';

import { useSocialAccountsStore } from './socialAccountsStore';
import { SocialPlatformIcon } from './SocialPlatformIcon';

export const CONSUMER_SOCIAL_PLATFORMS: ConsumerSocialPublishPlatform[] = [
  'instagram',
  'tiktok',
  'youtube',
  'x',
];

function connectionErrorKey(error: unknown) {
  const message = error instanceof Error ? error.message : '';
  const code = error instanceof Error && 'code' in error && typeof error.code === 'string' ? error.code : '';
  const detailed = {
    SOCIAL_OAUTH_PERMISSION_DENIED: 'permissionDenied',
    SOCIAL_OAUTH_NO_PAGES: 'noPages',
    SOCIAL_OAUTH_NO_INSTAGRAM_ACCOUNT: 'noInstagramAccount',
    SOCIAL_OAUTH_MULTIPLE_ACCOUNTS: 'multipleAccounts',
    SOCIAL_OAUTH_SESSION_EXPIRED: 'sessionExpired',
    SOCIAL_OAUTH_TOKEN_INVALID: 'tokenInvalid',
    SOCIAL_OAUTH_TOKEN_EXCHANGE_FAILED: 'tokenExchangeFailed',
    SOCIAL_OAUTH_PROFILE_FAILED: 'profileFailed',
    SOCIAL_OAUTH_PROVIDER_UNAVAILABLE: 'providerUnavailable',
    SOCIAL_OAUTH_RATE_LIMITED: 'rateLimited',
    SOCIAL_OAUTH_MALFORMED_CALLBACK: 'malformedCallback',
    SOCIAL_OAUTH_INTERNAL_ERROR: 'internalError',
  } as const;
  if (code in detailed) return detailed[code as keyof typeof detailed];
  if (message.includes('POPUP_BLOCKED')) return 'popupBlocked';
  if (message.includes('CANCELLED')) return 'cancelled';
  if (message.includes('NETWORK_OFFLINE') || code === 'NETWORK_ERROR') return 'networkOffline';
  if (message.includes('TIMEOUT') || code.includes('TIMEOUT')) return 'networkTimeout';
  if (message.includes('UNAVAILABLE')) return 'unavailableToast';
  return 'connectFailed';
}

export function SocialAccountManager({ compact = false }: { compact?: boolean }) {
  const t = useTranslations('socialPublishing.consumer.accounts');
  const platformT = useTranslations('socialPublishing.platforms');
  const commonT = useTranslations('common');
  const userId = useAuthStore(state => state.user?.id);
  const mutationLock = useRef(false);
  const disconnectingId = useSocialAccountsStore(state => state.disconnectingId);
  const accounts = useSocialAccountsStore((state) => state.accounts);
  const connectionStatus = useSocialAccountsStore((state) => state.connectionStatus);
  const isLoading = useSocialAccountsStore((state) => state.isLoading);
  const pendingPlatform = useSocialAccountsStore((state) => state.pendingPlatform);
  const error = useSocialAccountsStore((state) => state.error);
  const connectionError = useSocialAccountsStore((state) => state.connectionError);
  const fetchAccounts = useSocialAccountsStore((state) => state.fetchAccounts);
  const connect = useSocialAccountsStore((state) => state.connect);
  const disconnect = useSocialAccountsStore((state) => state.disconnect);
  const [confirmDisconnectId, setConfirmDisconnectId] = useState<string | null>(null);

  useEffect(() => {
    mutationLock.current = false;
    setConfirmDisconnectId(null);
    void fetchAccounts().catch(() => {});
  }, [fetchAccounts, userId]);

  const accountsByPlatform = useMemo(() => new Map(
    CONSUMER_SOCIAL_PLATFORMS.map((platform) => [
      platform,
      accounts.find((account) => account.platform === platform && account.auth_status === 'authorized'),
    ]),
  ), [accounts]);

  const handleConnect = useCallback(async (platform: ConsumerSocialPublishPlatform) => {
    if (mutationLock.current || pendingPlatform || disconnectingId || isLoading) return;
    const scope = getAuthScopeRevision();
    mutationLock.current = true;
    try {
      await connect(platform, { surface: compact ? 'publisher' : 'account-settings' });
      if (scope !== getAuthScopeRevision()) return;
      toast.success(t('connectedToast', { platform: platformT(platform) }));
    } catch (connectError) {
      if (scope !== getAuthScopeRevision()) return;
      toast.error(t(connectionErrorKey(connectError), { platform: platformT(platform) }));
    }
    finally { if (scope === getAuthScopeRevision()) mutationLock.current = false; }
  }, [compact, connect, disconnectingId, isLoading, pendingPlatform, platformT, t]);

  const handleDisconnect = useCallback(async (accountId: string, platform: ConsumerSocialPublishPlatform) => {
    if (mutationLock.current || pendingPlatform || disconnectingId || isLoading) return;
    if (confirmDisconnectId !== accountId) {
      setConfirmDisconnectId(accountId);
      return;
    }
    const scope = getAuthScopeRevision();
    mutationLock.current = true;
    try {
      await disconnect(accountId);
      if (scope !== getAuthScopeRevision()) return;
      toast.success(t('disconnectedToast', { platform: platformT(platform) }));
      setConfirmDisconnectId(null);
    } catch {
      if (scope !== getAuthScopeRevision()) return;
      toast.error(t('disconnectFailed', { platform: platformT(platform) }));
    }
    finally { if (scope === getAuthScopeRevision()) mutationLock.current = false; }
  }, [confirmDisconnectId, disconnect, disconnectingId, isLoading, pendingPlatform, platformT, t]);

  return (
    <div className={cn(styles.root, 'space-y-4', compact && 'space-y-2.5')}>
      {!compact ? (
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[#ECEDEF] pb-6">
          <div>
            <h3 className="text-[17px] font-semibold leading-6 text-[#25272B]">{t('title')}</h3>
            <p className="mt-1 max-w-[620px] text-[14px] leading-5 text-[#777C85]">{t('subtitle')}</p>
          </div>
          <StudioAction type="button"
            variant="secondary"
            size="compact"

            onClick={() => void fetchAccounts().catch(() => {})}
            disabled={isLoading || Boolean(pendingPlatform) || Boolean(disconnectingId)}
            className="shrink-0 gap-2"
            aria-busy={isLoading}
          >
            <RefreshCw className="h-4 w-4" />
            {t('refresh')}
          </StudioAction>
        </div>
      ) : null}

      {connectionError ? (
        <div role="alert" className="rounded-[10px] border border-semantic-error/20 bg-semantic-error-bg p-3 text-[13px] text-semantic-error">
          <p>{t(connectionErrorKey(connectionError), { platform: connectionError.platform ? platformT(connectionError.platform) : '' })}</p>
          {connectionError.referenceId ? <p className="mt-1">{t('errorReference', { id: connectionError.referenceId })}</p> : null}
        </div>
      ) : null}
      {error ? (
        <div role="alert" className="flex items-center gap-2 rounded-[10px] border border-semantic-error/20 bg-semantic-error-bg px-3 py-2.5 text-[13px] text-semantic-error">
          <XCircle className="h-4 w-4 shrink-0" aria-hidden />
          <span className="min-w-0 flex-1">{t('loadFailed')}</span>
          <StudioAction size="compact" type="button" variant="secondary" disabled={isLoading || Boolean(pendingPlatform) || Boolean(disconnectingId)} onClick={() => void fetchAccounts().catch(() => {})}>{commonT('retry')}</StudioAction>
        </div>
      ) : null}

      {isLoading && accounts.length === 0 && <p role="status" className="flex items-center gap-2 text-sm text-[#5B6A73]"><Loader2 className="h-4 w-4 animate-spin" aria-hidden />{commonT('loading')}</p>}
      <div className={cn('grid gap-3', compact ? 'grid-cols-1' : 'sm:grid-cols-2')}>
        {CONSUMER_SOCIAL_PLATFORMS.map((platform) => {
          const account = accountsByPlatform.get(platform);
          const pending = pendingPlatform === platform;
          const unavailable = connectionStatus?.[platform]?.configured === false;
          const confirming = confirmDisconnectId === account?.id;
          return (
            <section
              key={platform}
              className={cn(
                'rounded-[12px] border bg-white transition-colors',
                compact ? 'p-3' : 'p-4',
                account ? 'border-[#CFE8F2]' : 'border-[#E1E6ED]',
              )}
            >
              <div className="flex flex-wrap items-center gap-3">
                <SocialPlatformIcon platform={platform} className={compact ? 'h-9 w-9' : undefined} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="truncate text-[14px] font-semibold text-[#25272B]">{platformT(platform)}</p>
                    {account ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-[#169C5B]" aria-label={t('connected')} /> : null}
                  </div>
                  <p className="mt-0.5 truncate text-[12px] text-[#777C85]">
                    {account
                      ? (account.username ? `@${account.username.replace(/^@/, '')}` : account.display_name || t('connected'))
                      : unavailable
                        ? t('unavailableHint')
                        : t(platform === 'instagram' ? 'instagramHint' : platform === 'youtube' ? 'youtubeHint' : 'notConnected')}
                  </p>
                </div>
                {account ? (
                  <StudioAction type="button"
                    variant="secondary" size="compact"
                    disabled={isLoading || Boolean(pendingPlatform) || Boolean(disconnectingId)}
                    aria-busy={disconnectingId === account.id}
                    onClick={() => void handleDisconnect(account.id, platform)}
                    className={cn(
                      'shrink-0 gap-2',
                      confirming && styles.confirm,
                    )}
                  >
                    <Unplug className="h-3.5 w-3.5" aria-hidden />
                    {confirming ? t('confirmDisconnect') : t('disconnect')}
                  </StudioAction>
                ) : (
                  <StudioAction type="button"
                    size="compact"
                    variant="secondary"
                    onClick={() => void handleConnect(platform)}
                    aria-busy={pending}
                    disabled={isLoading || Boolean(disconnectingId) || Boolean(pendingPlatform) || unavailable || connectionStatus === null}
                    className="min-w-[84px] gap-2"
                  >
                    {pending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}{t(unavailable ? 'unavailable' : 'connect')}
                  </StudioAction>
                )}
              </div>
              {confirming && !disconnectingId && <div className="mt-3 flex justify-end"><StudioAction type="button" variant="secondary" size="compact" disabled={isLoading || Boolean(pendingPlatform)} onClick={() => setConfirmDisconnectId(null)}>{commonT('cancel')}</StudioAction></div>}
            </section>
          );
        })}
      </div>

      {!compact ? (
        <p className="text-[12px] leading-5 text-[#8B9098]">{t('privacyNote')}</p>
      ) : null}
    </div>
  );
}
